import { useState } from 'react';
import { Button } from '@rudi/ui/button';
import { toast } from 'sonner';
import { ChatModelFields, effortLabel } from '../components/ChatModelFields';
import {
  CHAT_BACKEND_LABELS,
  CHAT_BACKEND_ORDER,
  chatDefaultsError,
  useTeamChatDefaults,
  useUpdateTeamChatDefaults,
  type ChatExecutor,
  type TeamChatDefaults,
  type TeamChatDefaultSource,
  type UpdateTeamChatDefaults,
} from '../api/chat-defaults';

const FOLLOW_TENANT = 'inherit';

const EXECUTOR_SOURCE_TEXT: Record<TeamChatDefaultSource, string> = {
  team: 'Executor override saved for this team.',
  tenant: "Using the tenant's executor.",
  default: 'Using the platform executor default.',
};
const MODEL_SOURCE_TEXT: Record<TeamChatDefaultSource, string> = {
  team: 'Saved for this team and executor.',
  tenant: "Using the tenant's model for this executor.",
  default: 'Using the platform model default for this executor.',
};
const INHERITED_FROM: Record<TeamChatDefaultSource, string> = {
  team: 'team',
  tenant: 'tenant',
  default: 'platform default',
};

function TeamChatDefaultsForm({
  tenantId,
  teamId,
  defaults,
}: {
  tenantId: string;
  teamId: string;
  defaults: TeamChatDefaults;
}) {
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpdateTeamChatDefaults(tenantId, teamId);
  const model = defaults.ai_chat_model;
  const inheritedModel = defaults.inherited_ai_chat_model;
  const options = defaults.ai_chat_model_options;
  const executorValue =
    defaults.ai_chat_backend_source === 'team' ? defaults.ai_chat_backend : FOLLOW_TENANT;
  const inheritedOption = options.find(option => option.model === inheritedModel?.model);

  const save = async (payload: UpdateTeamChatDefaults, success: string) => {
    setError(null);
    try {
      await mutation.mutateAsync(payload);
      toast.success(success);
    } catch (e) {
      setError(chatDefaultsError(e));
    }
  };

  return (
    <div className="space-y-3">
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <label className="block space-y-1 text-sm">
        <span>Executor</span>
        <select
          aria-label="Team chat executor"
          className="h-9 w-full rounded-3xl border border-input bg-background px-3"
          value={executorValue}
          disabled={mutation.isPending}
          onChange={event => {
            const next = event.target.value;
            void save(
              { ai_chat_backend: next === FOLLOW_TENANT ? null : (next as ChatExecutor) },
              next === FOLLOW_TENANT
                ? 'Chat executor reset to tenant setting'
                : 'Chat executor updated'
            );
          }}
        >
          <option value={FOLLOW_TENANT}>
            Tenant setting ({CHAT_BACKEND_LABELS[defaults.inherited_ai_chat_backend]})
          </option>
          {executorValue === 'claude_code' && (
            <option value="claude_code" disabled>
              Claude Code CLI retired
            </option>
          )}
          {CHAT_BACKEND_ORDER.map(backend => (
            <option key={backend} value={backend}>
              {CHAT_BACKEND_LABELS[backend]}
            </option>
          ))}
        </select>
      </label>
      <p className="text-xs text-muted-foreground">
        {EXECUTOR_SOURCE_TEXT[defaults.ai_chat_backend_source]}
      </p>
      {!model && (
        <p className="text-sm text-muted-foreground">
          This Rudi connection does not report a model for this executor. You can still change the
          executor.
        </p>
      )}
      {model && (
        <div className="space-y-3">
          <ChatModelFields
            model={model.model}
            effort={model.effort}
            options={options}
            disabled={mutation.isPending}
            onChange={(next, effort) =>
              void save({ ai_chat_model: { model: next, effort } }, 'Chat model updated')
            }
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">{MODEL_SOURCE_TEXT[model.source]}</p>
            <Button
              size="sm"
              variant="outline"
              disabled={mutation.isPending || model.source !== 'team'}
              onClick={() =>
                void save({ ai_chat_model: null }, 'Chat model reset to tenant setting')
              }
            >
              Use tenant model
            </Button>
          </div>
          {model.source === 'team' && inheritedModel && (
            <p className="text-xs text-muted-foreground">
              Without this override: {inheritedOption?.label ?? inheritedModel.model}, reasoning
              level {effortLabel(inheritedModel.effort)}, from the{' '}
              {INHERITED_FROM[inheritedModel.source]}.
            </p>
          )}
          {defaults.ai_chat_backend === 'api' && (
            <p className="text-xs text-muted-foreground">
              OpenAI models use the tenant's OpenAI API key, which must have access to the selected
              model. OpenRouter models use Rudi's OpenRouter key, or an API key named openrouter.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function TeamChatDefaultsPanel({ tenantId, teamId }: { tenantId: string; teamId: string }) {
  const defaults = useTeamChatDefaults(tenantId, teamId);

  return (
    <section className="max-w-xl space-y-3 rounded-3xl border p-4">
      <div>
        <h2 className="text-sm font-medium">Chat defaults</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Choose the executor, model, and reasoning level for new chats in this team. The team
          setting overrides the tenant's. Existing chats keep their settings.
        </p>
      </div>
      {defaults.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading chat defaults…</p>
      ) : defaults.error ? (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-destructive">
            {chatDefaultsError(defaults.error)}
          </p>
          <Button size="sm" variant="outline" onClick={() => void defaults.refetch()}>
            Try again
          </Button>
        </div>
      ) : defaults.data ? (
        <TeamChatDefaultsForm tenantId={tenantId} teamId={teamId} defaults={defaults.data} />
      ) : (
        <p className="text-sm text-muted-foreground">
          This Rudi connection cannot set team chat defaults yet.
        </p>
      )}
    </section>
  );
}
